<template>
  <div class="image-manager pa-4">
    <v-row>
      <v-col
        v-for="imageType in imageTypes"
        :key="imageType.value"
        cols="12"
        md="6"
      >
        <v-card outlined class="image-type-card fill-height">
          <v-card-title class="section-title py-2 px-4">
            <v-icon small class="mr-2">{{ imageType.icon }}</v-icon>
            {{ imageType.text }}
            <v-chip small class="ml-2" color="grey lighten-2">{{ imagesByType(imageType.value).length }}</v-chip>
          </v-card-title>
          <v-divider></v-divider>

          <v-card-text class="pa-3">
            <div v-if="imagesByType(imageType.value).length" class="image-grid">
              <div
                v-for="img in imagesByType(imageType.value)"
                :key="img.id"
                class="image-item"
              >
                <div class="image-thumb" @click="openPreview(img)">
                  <v-img
                    v-if="img.expiring_url"
                    :src="img.expiring_url"
                    height="120"
                    contain
                    class="grey lighten-4 rounded"
                  >
                    <template #placeholder>
                      <v-row class="fill-height ma-0" align="center" justify="center">
                        <v-progress-circular indeterminate size="24" color="grey"></v-progress-circular>
                      </v-row>
                    </template>
                    <div class="image-overlay d-flex align-center justify-center">
                      <v-icon color="white">mdi-magnify-plus-outline</v-icon>
                    </div>
                  </v-img>
                </div>
                <div class="d-flex align-center mt-1">
                  <span class="text-caption text-truncate flex-grow-1" :title="img.file_name">{{ img.file_name }}</span>
                  <v-btn icon x-small color="error" @click="openDeleteDialog(img)">
                    <v-icon x-small>mdi-delete</v-icon>
                  </v-btn>
                </div>
              </div>
            </div>
            <div v-else class="no-images text-center py-6">
              <v-icon color="grey lighten-1" size="40">mdi-image-off-outline</v-icon>
              <div class="text-caption text--secondary mt-1">画像がありません</div>
            </div>
          </v-card-text>

          <v-divider></v-divider>
          <v-card-actions class="pa-3">
            <v-file-input
              v-model="selectedFiles[imageType.value]"
              :label="`画像をアップロード`"
              accept="image/*"
              prepend-icon="mdi-cloud-upload-outline"
              outlined
              dense
              hide-details
              class="upload-input"
              @change="uploadFile(imageType.value)"
            ></v-file-input>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <!-- 画像拡大モーダル -->
    <v-dialog v-model="previewDialog" max-width="900" content-class="preview-dialog">
      <v-card v-if="previewImage">
        <v-toolbar flat dense>
          <v-toolbar-title class="text-body-2">{{ previewImage.file_name }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="previewDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-divider></v-divider>
        <div class="preview-body pa-4 text-center">
          <v-img
            :src="previewImage.expiring_url"
            max-height="70vh"
            contain
          >
            <template #placeholder>
              <v-row class="fill-height ma-0" align="center" justify="center">
                <v-progress-circular indeterminate size="40" color="secondary"></v-progress-circular>
              </v-row>
            </template>
          </v-img>
        </div>
      </v-card>
    </v-dialog>

    <v-confirm-dialog
      ref="deleteConfirm"
      title="画像削除"
      :message="deleteMessage"
      buttonMessage="削除"
      @confirm="confirmDelete"
    />
  </div>
</template>

<script>
import VConfirmDialog from '@/components/atoms/ConfirmDialog'

export default {
  components: { VConfirmDialog },
  props: {
    contentImages: { type: Object, default: () => ({}) },
    contentId: { type: Number, required: true },
  },
  data() {
    return {
      imageTypes: [
        { text: '問題', value: 'question', icon: 'mdi-file-document-outline' },
        { text: '解答', value: 'answer', icon: 'mdi-check-circle-outline' },
        { text: '解答用紙', value: 'answer_sheet', icon: 'mdi-file-edit-outline' },
        { text: '台本', value: 'script', icon: 'mdi-script-text-outline' },
      ],
      selectedFiles: {
        question: null,
        answer: null,
        answer_sheet: null,
        script: null,
      },
      deletingImage: null,
      previewDialog: false,
      previewImage: null,
    }
  },
  computed: {
    deleteMessage() {
      return this.deletingImage
        ? `${this.deletingImage.file_name} を削除します。よろしいですか。`
        : ''
    },
  },
  methods: {
    imagesByType(type) {
      return this.contentImages[type] || []
    },
    openPreview(img) {
      this.previewImage = img
      this.previewDialog = true
    },
    async uploadFile(imageType) {
      const file = this.selectedFiles[imageType]
      if (!file) return

      const formData = new FormData()
      formData.append('file', file)
      formData.append('image_type', imageType)

      try {
        await this.$axios.post(
          `/api/v1/admin/contents/${this.contentId}/content_images`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        )
        this.selectedFiles[imageType] = null
        this.$toasted.global.success({ message: '画像をアップロードしました' })
        this.$emit('updated')
      } catch (e) {
        this.$toasted.global.error({ message: '画像のアップロードに失敗しました' })
      }
    },
    openDeleteDialog(image) {
      this.deletingImage = image
      this.$refs.deleteConfirm.open()
    },
    async confirmDelete() {
      if (!this.deletingImage) return
      try {
        await this.$axios.delete(
          `/api/v1/admin/contents/${this.contentId}/content_images/${this.deletingImage.id}`
        )
        this.$toasted.global.success({ message: '画像を削除しました' })
        this.$emit('updated')
      } catch (e) {
        this.$toasted.global.error({ message: '画像の削除に失敗しました' })
      }
      this.deletingImage = null
    },
  },
}
</script>

<style lang="stylus" scoped>
.image-manager
  max-width 1200px

.section-title
  font-size 14px
  font-weight bold

.image-type-card
  min-height 260px

.image-grid
  display grid
  grid-template-columns repeat(auto-fill, minmax(130px, 1fr))
  gap 12px

.image-thumb
  cursor pointer
  position relative
  border-radius 4px
  overflow hidden

  .image-overlay
    position absolute
    top 0
    left 0
    right 0
    bottom 0
    background rgba(0, 0, 0, 0.3)
    opacity 0
    transition opacity 0.2s ease

  &:hover .image-overlay
    opacity 1

.no-images
  min-height 100px
  display flex
  flex-direction column
  align-items center
  justify-content center

.upload-input
  max-width 100%

.preview-body
  background #f5f5f5
</style>
